<template>
  <v-row
    v-if="canAssignRole"
    align="start"
    class="ma-0"
    data-testid="userRolesSection-container"
    no-gutters
  >
    <v-col v-if="canAssignRole" class="pa-0 pe-5" cols="12" sm="5">
      <p class="text-caption blue-grey--text text--darken-1 ma-0 pa-0">
        {{ $t('organization_users.permission_other') }}
      </p>
      <v-menu auto bottom :close-on-content-click="false">
        <template v-slot:activator="{ on }">
          <div class="d-flex flex-wrap">
            <v-chip
              small
              outlined
              v-on="on"
              class="mt-1 me-2"
              v-for="role in roles"
              v-show="role.value"
              :key="role.id"
            >
              <span>{{ role.text }}</span>
              <v-icon
                class="ms-1"
                size="small"
                color="blue-grey"
                @click.stop=";(role.value = false), removeRole(role)"
                >mdi-close-circle-outline</v-icon
              >
            </v-chip>
          </div>
          <div v-show="!hasRoles">
            <v-btn
              small
              text
              rounded
              class="ms-n2"
              v-on="on"
              color="blue"
              data-testid="userRolesSection-button-selectPermission"
            >
              <v-icon left size="16" class="my-0 py-0"
                >mdi-plus-circle-outline
              </v-icon>
              {{ $t('common.select') }}
            </v-btn>
          </div>
        </template>
        <div class="white">
          <div
            v-for="permission in roles"
            class="menu-item ps-1 pe-3 py-2 mt-1 white"
            :key="permission.id"
          >
            <v-checkbox
              hide-details
              class="ma-0 pa-0"
              v-model="permission.value"
              :data-testid="`userRolesSection-checkbox-${permission.id}`"
              :ripple="false"
              @change="setRole(permission)"
            >
              <template v-slot:label>
                <span class="mx-0 px-0 font-size-13 font-weight-500">
                  {{ permission.text }}
                </span>
              </template>
            </v-checkbox>
          </div>
        </div>
      </v-menu>
    </v-col>

    <v-col v-if="canAssignRole && isAdminRoleEnabled" class="pa-0" cols="12" sm="7">
      <p class="text-caption blue-grey--text text--darken-1 ma-0 pa-0">
        {{ $t('common.admin_role_one') }}
      </p>

      <template>
        <v-radio-group
          v-model="selectedAdminRoleId"
          hide-details
          class="ma-0 pa-0"
          data-testid="userRolesSection-radioGroup-adminRole"
          @change="handleMultiSiteAdminRoleChange"
        >
          <v-radio
            v-for="role in adminRoles"
            :key="role.id"
            :data-testid="`userRolesSection-radio-${role.id}`"
            :ripple="false"
            :value="role.id"
          >
            <template v-slot:label>
              <span class="mx-0 px-0 font-size-13 font-weight-500">
                {{ role.text }}
              </span>
            </template>
          </v-radio>
        </v-radio-group>

        <div
          v-if="selectedAdminRoleId === ADMIN_ROLES.SITE_ADMIN"
          class="mt-3"
          data-testid="userRolesSection-section-siteScope"
        >
          <p
            v-if="!defaultSiteId"
            class="text-caption blue-grey--text text--darken-1 ma-0 pa-0"
            data-testid="userRolesSection-text-noSiteSelected"
          >
            {{ $t('organization_users.select_site_for_additional_scope') }}
          </p>

          <template v-else>
            <div
              v-if="defaultSiteName"
              class="mb-3"
              data-testid="userRolesSection-section-defaultSite"
            >
              <p
                class="text-caption blue-grey--text text--darken-1 ma-0 pa-0 mb-1"
              >
                {{ $t('organization_users.default_site') }}
              </p>
              <v-chip
                small
                outlined
                disabled
                class="mt-1"
                data-testid="userRolesSection-chip-defaultSite"
              >
                {{ defaultSiteName }}
              </v-chip>
            </div>

            <p
              class="text-body-2 font-weight-medium blue-grey--text text--darken-2 ma-0 pa-0 mb-1"
              data-testid="userRolesSection-text-optionalLabel"
            >
              {{ $t('organization_users.optional') }}
            </p>
            <p
              class="text-caption blue-grey--text text--darken-1 ma-0 pa-0 mb-3"
              data-testid="userRolesSection-text-additionalScopeDescription"
            >
              {{ $t('organization_users.additional_scope_description') }}
            </p>

            <MultipleSiteGroupSelector
              data-testid="userRolesSection-siteGroupSelector-managedSiteGroups"
              :options="siteGroups"
              :value="normalizedManagedSiteGroupIds"
              @change="$emit('change', { managedSiteGroups: $event })"
            />
            <MultipleSiteSelector
              class="mt-2"
              data-testid="userRolesSection-siteSelector-managedSites"
              :options="assignableSites"
              :value="normalizedManagedSiteIds"
              @change="$emit('change', { managedSites: $event })"
            />
          </template>
        </div>
      </template>
    </v-col>
  </v-row>
</template>
<script>
import { mapGetters } from 'vuex'
import { utils } from 'frontend-js-shared'

import { ADMIN_ROLES } from '@/constants'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import adminRoleMixin from '@/mixins/adminRoleMixin'

import MultipleSiteSelector from '@/components/MultipleSiteSelector.vue'
import MultipleSiteGroupSelector from '@/components/MultipleSiteGroupSelector.vue'

export default {
  name: 'UserRolesSection',

  mixins: [trackerMixin, adminRoleMixin],

  components: {
    MultipleSiteSelector,
    MultipleSiteGroupSelector,
  },

  props: {
    account: { type: Object, required: true },

    canAssignRole: {
      type: Boolean,
      required: false,
      default: false,
    },

    rolesEnabled: {
      type: Array,
      required: false,
      default() {
        return []
      },
    },

    defaultSiteId: {
      type: String,
      required: false,
      default: '',
    },

    sites: {
      type: Array,
      default: null,
    },

    siteGroups: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      roles: [
        {
          value: this.account?.isCoach ?? false,
          text: this.$t('common.roles.coach_one'),
          id: 'isCoach',
        },
        {
          value: this.account?.canDoFinancialValidation ?? false,
          text: this.$t('common.roles.impact_validator'),
          id: 'canDoFinancialValidation',
        },
        {
          value: this.account?.isRoutineManager ?? false,
          text: this.$t('common.roles.routine_manager'),
          id: 'isRoutineManager',
        },
      ],
      selectedAdminRoleId: ADMIN_ROLES.NONE,
    }
  },

  created() {
    this.selectedAdminRoleId = this.adminRole.id
  },

  computed: {
    ...mapGetters(['organizationSites']),

    adminRole() {
      return this.getAdminRole(this.account)
    },

    isAdminRoleEnabled() {
      return this.rolesEnabled.includes(ADMIN_ROLES.ORGANIZATION_ADMIN)
    },

    hasRoles() {
      return Boolean(this.roles.some(p => p.value === true))
    },

    defaultSite() {
      const sites = this.sites ?? this.organizationSites

      return (
        sites.find(site => utils.getEntityId(site) === this.defaultSiteId) ??
        null
      )
    },

    defaultSiteName() {
      return this.defaultSite?.name ?? null
    },

    assignableSites() {
      const sites = this.sites ?? this.organizationSites

      return sites.filter(
        site => utils.getEntityId(site) !== this.defaultSiteId
      )
    },

    normalizedManagedSiteGroupIds() {
      return utils.getManagedSiteGroups(this.account)
    },

    normalizedManagedSiteIds() {
      return utils.getManagedSites(this.account)
    },
  },

  watch: {
    defaultSiteId(newId) {
      if (!newId) {
        this.$emit('change', {
          managedSites: [],
          managedSiteGroups: [],
        })
      }
    },
  },

  methods: {
    setRole(role) {
      this.trackAddPeopleNavigate({
        element: 'roleSelector',
        section: 'userForm',
        updatedRole: {
          [`${role.id}`]: role.value,
        },
      })
      this.$emit('change', {
        [`${role.id}`]: role.value,
      })
    },

    removeRole(permissionToRemove) {
      const data = {}

      data[permissionToRemove.id] = false

      this.$emit('change', data)
    },

    handleMultiSiteAdminRoleChange(roleId) {
      const data = {}

      this.adminRoles.forEach(adminRole => {
        if (adminRole.id !== ADMIN_ROLES.NONE)
          data[adminRole.id] = adminRole.id === roleId
      })

      data.managedSites = []
      data.managedSiteGroups = []

      this.trackAddPeopleNavigate({
        element: 'adminRoleSelector',
        section: 'userForm',
        adminRoles: data,
      })

      this.$emit('change', data)
    },
  },
}
</script>
